/**
 * Static path → SiteHeader title / breadcrumbs.
 * More specific patterns must come first.
 * Pages can override the last crumb label via usePageTitle / PageHeader title prop.
 */

const ROUTE_TITLES = [
  { test: (p) => p === "/platform", title: "Platform Dashboard" },
  { test: (p) => p === "/", title: "Organization Dashboard" },
  { test: (p) => p === "/schedule", title: "Weekly Schedule" },
  { test: (p) => p.startsWith("/visits/"), title: "Visit" },
  { test: (p) => p === "/care-plans/templates/new", title: "New Care Plan Template" },
  { test: (p) => /^\/care-plans\/templates\/[^/]+$/.test(p), title: "Edit Care Plan Template" },
  { test: (p) => p === "/care-plans/templates", title: "Care Plan Templates" },
  { test: (p) => p === "/care-plans", title: "Client Care Plans" },
  { test: (p) => p === "/audit-logs", title: "Access & Audit Logs" },
  { test: (p) => p === "/account", title: "Account" },
  { test: (p) => p === "/settings/appearance", title: "Appearance" },
  { test: (p) => p === "/settings/users", title: "Team Members" },
  { test: (p) => p === "/settings/org", title: "Organization Settings" },
  { test: (p) => p === "/clients/new", title: "New Client" },
  { test: (p) => /^\/clients\/[^/]+$/.test(p), title: "Client" },
  { test: (p) => p === "/clients", title: "Clients" },
  { test: (p) => /^\/caregivers\/[^/]+\/schedule$/.test(p), title: "Caregiver Schedule" },
  { test: (p) => p === "/caregivers/new", title: "New Caregiver" },
  { test: (p) => /^\/caregivers\/[^/]+$/.test(p), title: "Caregiver" },
  { test: (p) => p === "/caregivers", title: "Caregivers" },
  { test: (p) => p === "/admin/agencies/new", title: "New Organization" },
  { test: (p) => /^\/admin\/agencies\/[^/]+$/.test(p), title: "Organization" },
  { test: (p) => p === "/admin/agencies", title: "Organizations" },
  { test: (p) => p === "/admin/users/new", title: "New User" },
  { test: (p) => /^\/admin\/users\/[^/]+$/.test(p), title: "User" },
  { test: (p) => p === "/admin/users", title: "Users" },
  { test: (p) => p === "/admin/roles", title: "Roles & Permissions" },
  { test: (p) => p === "/help", title: "Get Help" },
  { test: (p) => /^\/docs\/[^/]+$/.test(p), title: "Guide" },
  { test: (p) => p === "/docs", title: "Docs" },
];

/**
 * Path → breadcrumb trail. Parents have href; the last item is the current page.
 * Ordered most-specific first, matching ROUTE_TITLES.
 */
const ROUTE_BREADCRUMBS = [
  { test: (p) => p === "/platform", crumbs: [{ label: "Platform Dashboard" }] },
  { test: (p) => p === "/", crumbs: [{ label: "Organization Dashboard" }] },
  { test: (p) => p === "/schedule", crumbs: [{ label: "Weekly Schedule" }] },
  {
    test: (p) => p.startsWith("/visits/"),
    crumbs: [
      { label: "Weekly Schedule", href: "/schedule" },
      { label: "Visit" },
    ],
  },
  {
    test: (p) => p === "/care-plans/templates/new",
    crumbs: [
      { label: "Care Plans", href: "/care-plans" },
      { label: "Templates", href: "/care-plans/templates" },
      { label: "New Care Plan Template" },
    ],
  },
  {
    test: (p) => /^\/care-plans\/templates\/[^/]+$/.test(p),
    crumbs: [
      { label: "Care Plans", href: "/care-plans" },
      { label: "Templates", href: "/care-plans/templates" },
      { label: "Edit Care Plan Template" },
    ],
  },
  {
    test: (p) => p === "/care-plans/templates",
    crumbs: [
      { label: "Care Plans", href: "/care-plans" },
      { label: "Care Plan Templates" },
    ],
  },
  { test: (p) => p === "/care-plans", crumbs: [{ label: "Client Care Plans" }] },
  { test: (p) => p === "/audit-logs", crumbs: [{ label: "Access & Audit Logs" }] },
  { test: (p) => p === "/account", crumbs: [{ label: "Account" }] },
  {
    test: (p) => p === "/settings/appearance",
    crumbs: [
      { label: "Settings", href: "/settings/org" },
      { label: "Appearance" },
    ],
  },
  {
    test: (p) => p === "/settings/users",
    crumbs: [
      { label: "Settings", href: "/settings/org" },
      { label: "Team Members" },
    ],
  },
  {
    test: (p) => p === "/settings/org",
    crumbs: [
      { label: "Settings", href: "/settings/org" },
      { label: "Organization Settings" },
    ],
  },
  {
    test: (p) => p === "/clients/new",
    crumbs: [
      { label: "Clients", href: "/clients" },
      { label: "New Client" },
    ],
  },
  {
    test: (p) => /^\/clients\/[^/]+$/.test(p),
    crumbs: [
      { label: "Clients", href: "/clients" },
      { label: "Client" },
    ],
  },
  { test: (p) => p === "/clients", crumbs: [{ label: "Clients" }] },
  {
    test: (p) => /^\/caregivers\/[^/]+\/schedule$/.test(p),
    crumbs: (p) => {
      const id = p.split("/")[2];
      return [
        { label: "Caregivers", href: "/caregivers" },
        { label: "Caregiver", href: `/caregivers/${id}` },
        { label: "Schedule" },
      ];
    },
  },
  {
    test: (p) => p === "/caregivers/new",
    crumbs: [
      { label: "Caregivers", href: "/caregivers" },
      { label: "New Caregiver" },
    ],
  },
  {
    test: (p) => /^\/caregivers\/[^/]+$/.test(p),
    crumbs: [
      { label: "Caregivers", href: "/caregivers" },
      { label: "Caregiver" },
    ],
  },
  { test: (p) => p === "/caregivers", crumbs: [{ label: "Caregivers" }] },
  {
    test: (p) => p === "/admin/agencies/new",
    crumbs: [
      { label: "Organizations", href: "/admin/agencies" },
      { label: "New Organization" },
    ],
  },
  {
    test: (p) => /^\/admin\/agencies\/[^/]+$/.test(p),
    crumbs: [
      { label: "Organizations", href: "/admin/agencies" },
      { label: "Organization" },
    ],
  },
  { test: (p) => p === "/admin/agencies", crumbs: [{ label: "Organizations" }] },
  {
    test: (p) => p === "/admin/users/new",
    crumbs: [
      { label: "Users", href: "/admin/users" },
      { label: "New User" },
    ],
  },
  {
    test: (p) => /^\/admin\/users\/[^/]+$/.test(p),
    crumbs: [
      { label: "Users", href: "/admin/users" },
      { label: "User" },
    ],
  },
  { test: (p) => p === "/admin/users", crumbs: [{ label: "Users" }] },
  { test: (p) => p === "/admin/roles", crumbs: [{ label: "Roles & Permissions" }] },
  { test: (p) => p === "/help", crumbs: [{ label: "Get Help" }] },
  {
    test: (p) => /^\/docs\/[^/]+$/.test(p),
    crumbs: [
      { label: "Docs", href: "/docs" },
      { label: "Guide" },
    ],
  },
  { test: (p) => p === "/docs", crumbs: [{ label: "Docs" }] },
];

export function titleFromPath(pathname) {
  for (const route of ROUTE_TITLES) {
    if (route.test(pathname)) return route.title;
  }
  const segment = pathname.split("/").filter(Boolean)[0] || "Dashboard";
  return segment
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * @returns {{ label: string, href?: string }[]}
 */
export function breadcrumbsFromPath(pathname) {
  for (const route of ROUTE_BREADCRUMBS) {
    if (route.test(pathname)) {
      const crumbs = typeof route.crumbs === "function" ? route.crumbs(pathname) : route.crumbs;
      return crumbs.map((c) => ({ ...c }));
    }
  }
  return [{ label: titleFromPath(pathname) }];
}
